import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import { ServerError, ValidationError } from '@/domain/errors';
import { type IAnalytics } from '@/services/Analytics';
import { type IAppBus } from '@/services/AppBus';
import { type IBookParser } from '@/services/BookParser';
import { type II18n } from '@/services/i18n/I18n';
import { type ILocalData } from '@/services/LocalData';
import { type ILogger } from '@/services/Logger';

const BOOK_STORE = 'book';
const PROGRESS_STORE = 'progress';
const CURRENT_KEY = 'current';

/**
 * Owns the "get a book into the app" flow: parse the uploaded file, persist it, and announce it on
 * the bus so the player picks it up. Single-book app, so a successful upload replaces whatever was
 * there. Parsing errors surface inline (and as a toast); they do not crash the app.
 */
export class UploadStore {
  private _isParsing = false;
  private _error: string | null = null;

  constructor(
    private bus: IAppBus,
    private parser: IBookParser,
    private localData: ILocalData,
    private analytics: IAnalytics,
    private i18n: II18n,
    private logger: ILogger,
  ) {
    makeAutoObservable(this);
  }

  get isParsing(): boolean {
    return this._isParsing;
  }

  get error(): string | null {
    return this._error;
  }

  upload = async (file: File): Promise<void> => {
    this._error = null;
    this._isParsing = true;
    try {
      const parsed = await this.parser.parse(file);
      await this.localData.put(BOOK_STORE, CURRENT_KEY, parsed);
      await this.localData.delete(PROGRESS_STORE, CURRENT_KEY);
      this.bus.bookLoaded(parsed.title);
      this.bus.toast('success', this.i18n.t('toast.nowReading', { title: parsed.title }));
      this.analytics.bookAdded(parsed.format);
    } catch (error) {
      if (error instanceof ValidationError || error instanceof ServerError) {
        this.logger.warn('UploadStore.upload', error);
        // Show a localized, generic message to the user; keep the detailed English one for analytics.
        this._error = this.i18n.t('toast.loadFailed');
        this.bus.toast('error', this._error);
        this.analytics.error(error.message);
        return;
      }
      throw error;
    } finally {
      this._isParsing = false;
    }
  };

  clearBook = async (): Promise<void> => {
    await this.localData.delete(BOOK_STORE, CURRENT_KEY);
    await this.localData.delete(PROGRESS_STORE, CURRENT_KEY);
    this.bus.bookCleared();
  };
}

export const TOKEN_UploadStore = token<UploadStore>('UploadStore');
